import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Calendar, Clock, MapPin, Phone, Mail, User, FileText, Zap, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Quote Request Dialog Component
export function QuoteRequestDialog({ 
  open, 
  onOpenChange, 
  service, 
  subcategory,
  provider = null, // Optional: specific provider
  country = 'UK',
}) {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [requestNumber, setRequestNumber] = useState('');
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    description: '',
    preferred_date: '',
    preferred_time: '',
    is_emergency: false,
    budget_range: '',
    location: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/trades-services/quote-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          subcategory_slug: subcategory?.slug || subcategory,
          service_key: service?.key || service,
          service_name: service?.name || service,
          country: country,
          provider_id: provider?.id || null,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
        setRequestNumber(data.request_number);
        toast.success('Quote request submitted successfully!');
      } else {
        toast.error(data.detail || 'Failed to submit request');
      }
    } catch (error) {
      console.error('Failed to submit quote request:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      customer_address: '',
      description: '',
      preferred_date: '',
      preferred_time: '',
      is_emergency: false,
      budget_range: '',
      location: '',
    });
    setSubmitted(false);
    setRequestNumber('');
    onOpenChange(false);
  };

  const currencySymbol = country === 'UK' ? '£' : 'Rs.';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Request a Quote
          </DialogTitle>
          <DialogDescription>
            {service?.name || 'Service'} - Get free quotes from local professionals
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <SuccessView 
            requestNumber={requestNumber} 
            onClose={handleClose}
            provider={provider}
          />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Service Info */}
            <div className="p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xl">{service?.icon || '🔧'}</span>
                <span className="font-medium">{service?.name}</span>
                {service?.allow_emergency && (
                  <Badge className="bg-red-100 text-red-700 text-xs">Emergency Available</Badge>
                )}
              </div>
              {provider && (
                <p className="text-sm text-blue-600">
                  From: {provider.name} ({provider.service_area})
                </p>
              )}
              {!provider && (
                <p className="text-sm text-blue-600">
                  Your request will be sent to all available providers in your area
                </p>
              )}
            </div>

            {/* Emergency Toggle */}
            {service?.allow_emergency && (
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-red-500" />
                  <div>
                    <p className="font-medium text-red-700">Emergency Service</p>
                    <p className="text-xs text-red-600">Request urgent 24/7 assistance</p>
                  </div>
                </div>
                <Switch
                  checked={formData.is_emergency}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_emergency: checked })}
                />
              </div>
            )}

            {/* Customer Details */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-gray-700">Your Details</h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="name" className="text-xs">Full Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="name"
                      placeholder="John Smith"
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone" className="text-xs">Phone *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+44 7700 123456"
                      value={formData.customer_phone}
                      onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-xs">Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="location" className="text-xs">Location / Postcode *</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="location"
                    placeholder="e.g., London SW1A 1AA"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="address" className="text-xs">Full Address (Optional)</Label>
                <Input
                  id="address"
                  placeholder="123 Main Street, Apartment 4B"
                  value={formData.customer_address}
                  onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })}
                />
              </div>
            </div>

            {/* Job Details */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-gray-700">Job Details</h4>
              
              <div>
                <Label htmlFor="description" className="text-xs">Describe what you need *</Label>
                <Textarea
                  id="description"
                  placeholder="Please describe the work you need done, including any specific requirements or issues..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="date" className="text-xs">Preferred Date</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="date"
                      type="date"
                      value={formData.preferred_date}
                      onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                      className="pl-10"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="time" className="text-xs">Preferred Time</Label>
                  <Select
                    value={formData.preferred_time}
                    onValueChange={(value) => setFormData({ ...formData, preferred_time: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="morning">Morning (8am-12pm)</SelectItem>
                      <SelectItem value="afternoon">Afternoon (12pm-5pm)</SelectItem>
                      <SelectItem value="evening">Evening (5pm-8pm)</SelectItem>
                      <SelectItem value="flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="budget" className="text-xs">Budget Range (Optional)</Label>
                <Select
                  value={formData.budget_range}
                  onValueChange={(value) => setFormData({ ...formData, budget_range: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select budget range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under_100">Under {currencySymbol}100</SelectItem>
                    <SelectItem value="100_250">{currencySymbol}100 - {currencySymbol}250</SelectItem>
                    <SelectItem value="250_500">{currencySymbol}250 - {currencySymbol}500</SelectItem>
                    <SelectItem value="500_1000">{currencySymbol}500 - {currencySymbol}1,000</SelectItem>
                    <SelectItem value="over_1000">Over {currencySymbol}1,000</SelectItem>
                    <SelectItem value="flexible">Flexible / Not sure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Request Quote
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Success View Component
function SuccessView({ requestNumber, onClose, provider }) {
  return (
    <div className="text-center py-6">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <CheckCircle className="w-8 h-8 text-green-600" />
      </div>
      <h3 className="text-lg font-semibold text-green-700 mb-2">
        Quote Request Submitted!
      </h3>
      <p className="text-gray-600 mb-4">
        Your request number is:
      </p>
      <div className="bg-gray-100 rounded-lg p-3 mb-4">
        <code className="text-lg font-mono font-bold">{requestNumber}</code>
      </div>
      <div className="text-sm text-gray-500 space-y-2 mb-6">
        {provider ? (
          <p>
            <strong>{provider.name}</strong> will review your request and respond shortly.
          </p>
        ) : (
          <p>
            Local providers will review your request and send you their quotes.
          </p>
        )}
        <p>
          We'll notify you via email when you receive quotes.
        </p>
      </div>
      <Button onClick={onClose} className="w-full">
        Done
      </Button>
    </div>
  );
}

// Quick Quote Button Component
export function QuickQuoteButton({ 
  service, 
  subcategory, 
  provider = null,
  country = 'UK',
  variant = 'default',
  size = 'default',
  className = '',
}) {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setDialogOpen(true)}
        className={className}
      >
        <FileText className="w-4 h-4 mr-2" />
        Get Quote
      </Button>
      
      <QuoteRequestDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        service={service}
        subcategory={subcategory}
        provider={provider}
        country={country}
      />
    </>
  );
}

// Service Card with Quote Button
export function ServiceCardWithQuote({ service, subcategory, country = 'UK' }) {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <div
        className={`p-3 rounded-lg border text-sm transition-all hover:shadow-md cursor-pointer ${
          service.allow_emergency
            ? 'bg-red-50 border-red-200 hover:border-red-300'
            : 'bg-gray-50 border-gray-200 hover:border-gray-300'
        }`}
        onClick={() => setDialogOpen(true)}
      >
        <div className="flex items-start gap-2">
          <span className="text-xl flex-shrink-0">{service.icon}</span>
          <div className="flex-1 min-w-0">
            <span className="font-medium block">{service.name}</span>
            {service.description && (
              <span className="text-xs text-gray-500 block">{service.description}</span>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between mt-2">
          {service.allow_emergency && (
            <Badge className="bg-red-100 text-red-700 text-xs">
              🆘 Emergency
            </Badge>
          )}
          <Button size="sm" variant="outline" className="text-xs h-7 ml-auto">
            <FileText className="w-3 h-3 mr-1" />
            Get Quote
          </Button>
        </div>
      </div>
      
      <QuoteRequestDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        service={service}
        subcategory={subcategory}
        country={country}
      />
    </>
  );
}

export default QuoteRequestDialog;
